import React from 'react';

/**
 * Global Error Boundary
 * Catches Radix UI portal "removeChild" DOM errors that happen during
 * React Router navigation when a Popover/Dialog is open.
 * For these harmless DOM cleanup errors we silently recover.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error) {
    // Only crash the app for "real" errors, not DOM portal cleanup glitches
    const isPortalCleanupError =
      error?.message?.includes('removeChild') ||
      error?.message?.includes('NotFoundError') ||
      error?.message?.includes('The node to be removed') ||
      error?.message?.includes('not a child of this node');

    if (isPortalCleanupError) {
      // Recoverable: schedule a reset, don't render error UI
      return { hasError: true, recoverable: true, errorMessage: error.message };
    }
    return { hasError: true, recoverable: false, errorMessage: error.message };
  }

  componentDidCatch(error, info) {
    const isPortalCleanupError =
      error?.message?.includes('removeChild') ||
      error?.message?.includes('NotFoundError') ||
      error?.message?.includes('The node to be removed') ||
      error?.message?.includes('not a child of this node');

    if (isPortalCleanupError) {
      // Auto-recover immediately for portal cleanup errors
      setTimeout(() => this.setState({ hasError: false, recoverable: false, errorMessage: '' }), 0);
    }
  }

  render() {
    if (this.state.hasError && !this.state.recoverable) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-8 text-center">
          <div className="max-w-md">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Bir şeyler ters gitti</h2>
            <p className="text-sm text-gray-500 mb-4">Sayfayı yenileyerek tekrar deneyin.</p>
            <button
              onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 transition-colors"
            >
              Yenile
            </button>
          </div>
        </div>
      );
    }

    // Either no error, or recovering from portal cleanup error (render children normally)
    return this.props.children;
  }
}
